import {type FetchSuggestFunction, Grid, List, Suggest, SuggestProps, SuggestValue} from '@logistics-frontend/blocks'
import type {SuggestObject, SuggestResponse} from '@yandex-delivery/api/repo/suggest/types'
import {DISABLE_AUTOCOMPLETE, useController, useFormContext} from '@yandex-delivery/form'
import type {Point} from '@yandex-taxi/js-integration-api'
import clsx from 'clsx'
import React from 'react'

import * as classes from './classes'
import {toAddressSuggestState} from './mappers/to-address-suggest-state'

type Props = Pick<SuggestProps, 'className' | 'label' | 'lead' | 'size' | 'trail' | 'variant'> & {
  fetchSuggest: FetchSuggestFunction<SuggestObject, SuggestResponse>
  helperText?: string
  loadingText?: string
  location: Point
  name: string
  noItemsText?: string
  placeholder?: string
  pointType: 'a' | 'b'
  renderPhoto?: (photoUrl: string) => React.ReactElement
}

export function AddressSuggest(props: Props): React.ReactElement<Props> {
  const {
    className,
    fetchSuggest,
    helperText,
    label,
    lead,
    loadingText,
    location,
    name,
    noItemsText,
    placeholder,
    pointType,
    renderPhoto,
    size,
    trail,
    variant,
  } = props

  const {control} = useFormContext()
  const {field, fieldState} = useController({name, control})

  function changeHandler(suggestObject?: SuggestObject, inputValue?: string) {
    if (suggestObject) {
      field.onChange(toAddressSuggestState(suggestObject))
      field.onBlur()
    } else if (inputValue?.length === 0) {
      field.onChange(toAddressSuggestState(undefined))
    }
  }

  function blurHandler() {
    // Такой хак нужен для того, чтобы onBlur срабатывал после выбора адреса из списка
    // Так как в этом случае сначала срабатывает onChange, а потом onBlur
    // Список адресов находится вне input и поэтому onBlur срабатывает раньше, чем выбор адреса
    // Это триггерит ошибку
    window.setTimeout(field.onBlur, 1000 / 60)
  }

  function handleRenderItem(point: SuggestValue) {
    return renderItem(point, {renderPhoto})
  }

  return (
    <Suggest<SuggestObject>
      autoCapitalize="none"
      autoComplete={DISABLE_AUTOCOMPLETE}
      className={className}
      defaultText={field.value?.fullName}
      error={fieldState.invalid}
      fetchSuggest={fetchSuggest}
      helperText={fieldState.error?.message || helperText}
      label={label}
      lead={lead}
      loadingText={loadingText}
      location={location}
      noItemsText={noItemsText}
      onBlur={blurHandler}
      onChange={changeHandler}
      placeholder={placeholder}
      point={field.value?.coordinates}
      pointType={pointType}
      renderItem={handleRenderItem}
      size={size}
      spellCheck="false"
      trail={trail}
      variant={variant}
      resetInputOnBlur
    />
  )
}

type ItemSettings = {
  renderPhoto?: (photoUrl: string) => React.ReactElement
}

function renderItem(point: SuggestValue, {renderPhoto}: ItemSettings) {
  return (
    <Grid alignItems="center" container>
      {point.photoUrl && renderPhoto && (
        <Grid className={classes.image} item>
          {renderPhoto(point.photoUrl)}
        </Grid>
      )}
      <Grid
        className={clsx(classes.content, {[classes.withImage]: !!point.photoUrl})}
        direction="column"
        container
        item
      >
        <Grid item xs>
          <List.Label className={classes.truncate} variant="body2">
            {point.title}
          </List.Label>
        </Grid>
        <Grid item xs>
          <List.Description color="minor" variant="caption1">
            {point.subtitle}
          </List.Description>
        </Grid>
        <Grid className={classes.tail} item>
          <List.Description color="minor" variant="caption1">
            {point.distance}
          </List.Description>
        </Grid>
      </Grid>
    </Grid>
  )
}
